import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, BoxIcon, SearchIcon, EyeIcon, EditIcon, AlertCircleIcon, CalendarIcon, PackageIcon, PaletteIcon, BriefcaseIcon, FileTextIcon, CheckCircleIcon, TruckIcon, XCircleIcon, PlusIcon, ChevronDownIcon, MoreVerticalIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// --- Types ---
type ProductType = 'book' | 'merch' | 'event' | 'bookbox' | 'service' | 'creative' | 'article';
type PreOrderStatus = 'active' | 'upcoming' | 'fulfilled' | 'cancelled';
interface PreOrder {
  id: string;
  title: string;
  type: ProductType;
  image: string;
  preOrderPrice: number;
  regularPrice: number;
  orders: number;
  releaseDate: string;
  status: PreOrderStatus;
  isApproaching?: boolean;
}
// --- Mock Data ---
const mockPreOrders: PreOrder[] = [{
  id: 'PRE-001',
  title: 'The Silent Echo',
  type: 'book',
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 11.99,
  regularPrice: 14.99,
  orders: 128,
  releaseDate: '2023-11-15',
  status: 'active'
}, {
  id: 'PRE-002',
  title: 'Fantasy Lovers Box',
  type: 'bookbox',
  image: 'https://images.unsplash.com/photo-1549880338-65ddcdfd017b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 39.99,
  regularPrice: 49.99,
  orders: 45,
  releaseDate: '2023-10-20',
  status: 'active',
  isApproaching: true
}, {
  id: 'PRE-003',
  title: 'Whispers in the Dark',
  type: 'book',
  image: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 9.99,
  regularPrice: 12.99,
  orders: 0,
  releaseDate: '2024-02-14',
  status: 'upcoming'
}, {
  id: 'PRE-004',
  title: 'Echo Series Hoodie',
  type: 'merch',
  image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 19.99,
  regularPrice: 24.99,
  orders: 32,
  releaseDate: '2023-11-20',
  status: 'active'
}, {
  id: 'PRE-005',
  title: 'Midnight Launch Party',
  type: 'event',
  image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 0,
  regularPrice: 0,
  orders: 67,
  releaseDate: '2024-01-15',
  status: 'upcoming'
}, {
  id: 'PRE-006',
  title: 'Beyond the Veil',
  type: 'book',
  image: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 10.99,
  regularPrice: 13.99,
  orders: 256,
  releaseDate: '2023-10-01',
  status: 'fulfilled'
}, {
  id: 'PRE-007',
  title: 'Dark Romance Box',
  type: 'bookbox',
  image: 'https://images.unsplash.com/photo-1607344645866-009c320b63e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 44.99,
  regularPrice: 54.99,
  orders: 12,
  releaseDate: '2023-12-15',
  status: 'cancelled'
}, {
  id: 'PRE-008',
  title: 'The Last Kingdom',
  type: 'book',
  image: 'https://images.unsplash.com/photo-1531988042231-d39a9cc12a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  preOrderPrice: 12.99,
  regularPrice: 15.99,
  orders: 89,
  releaseDate: '2023-12-10',
  status: 'active'
}];
// --- Helper Components ---
const StatusBadge = ({
  status
}: {
  status: PreOrderStatus;
}) => {
  const styles = {
    active: 'bg-green-100 text-green-800 border-green-200',
    upcoming: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    fulfilled: 'bg-blue-100 text-blue-800 border-blue-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200'
  };
  const labels = {
    active: 'Active',
    upcoming: 'Upcoming',
    fulfilled: 'Fulfilled',
    cancelled: 'Cancelled'
  };
  const rt = useRoleTheme();
  return <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium border ${styles[status]}`} data-id="element-19722" data-mp="i7mntjwtgzye">
      {status === 'active' && <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-19723" data-mp="88nr8zpbsurr" />}
      {status === 'upcoming' && <CalendarIcon className="w-3 h-3 mr-1" data-id="element-19724" data-mp="8artld7ktlpx" />}
      {status === 'fulfilled' && <TruckIcon className="w-3 h-3 mr-1" data-id="element-19725" data-mp="3avo19j8dm89" />}
      {status === 'cancelled' && <XCircleIcon className="w-3 h-3 mr-1" data-id="element-19726" data-mp="3unqfdptsrs16" />}
      {labels[status]}
    </span>;
};
const TypeBadge = ({
  type
}: {
  type: ProductType;
}) => {
  const styles = {
    book: 'bg-purple-100 text-purple-800',
    merch: 'bg-amber-100 text-amber-800',
    event: 'bg-teal-100 text-teal-800',
    bookbox: 'bg-pink-100 text-pink-800',
    service: 'bg-blue-100 text-blue-800',
    creative: 'bg-violet-100 text-violet-800',
    article: 'bg-emerald-100 text-emerald-800'
  };
  const icons = {
    book: BookIcon,
    merch: BoxIcon,
    event: CalendarIcon,
    bookbox: PackageIcon,
    service: BriefcaseIcon,
    creative: PaletteIcon,
    article: FileTextIcon
  };
  const Icon = icons[type];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[type]}`} data-id="element-19727" data-mp="3bkerdetpqqrf">
      <Icon className="w-3 h-3 mr-1" data-id="element-19728" data-mp="3ghy5meqev3jd" />
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </span>;
};
const PreOrders = () => {
  const dk = useDarkClasses();
  const [activeType, setActiveType] = useState<ProductType | 'all'>('all');
  const [activeStatus, setActiveStatus] = useState<PreOrderStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter logic
  const filteredPreOrders = mockPreOrders.filter(item => {
    const matchesType = activeType === 'all' || item.type === activeType;
    const matchesStatus = activeStatus === 'all' || item.status === activeStatus;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });
  // Stats
  const stats = {
    total: mockPreOrders.length,
    active: mockPreOrders.filter(i => i.status === 'active').length,
    upcoming: mockPreOrders.filter(i => i.status === 'upcoming').length,
    fulfilled: mockPreOrders.filter(i => i.status === 'fulfilled').length,
    approaching: mockPreOrders.filter(i => i.isApproaching).length
  };
  return <div className={`min-h-screen pb-12 ${dk.page}`} data-id="element-19729" data-mp="194wdxcvzhk6r">
      {/* Alert Banner */}
      {stats.approaching > 0 && <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-id="element-19730" data-mp="1o270bha60ah9">
          <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-19731" data-mp="1j2u2y2lo3l0f">
            <div className="flex items-center text-amber-800" data-id="element-19732" data-mp="1j4wz74deyadl">
              <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-19733" data-mp="2zfr7otg5qm1y" />
              <span className="text-sm font-medium" data-id="element-19734" data-mp="2ui98vvvutn7o">
                {stats.approaching} pre-order{stats.approaching > 1 ? 's' : ''}{' '}
                releasing within 7 days — prepare for fulfillment.
              </span>
            </div>
            <button onClick={() => {
          setActiveStatus('active');
          setSearchQuery('');
        }} className="text-sm font-medium text-amber-900 underline hover:text-amber-700" data-id="element-19735" data-mp="371ebquffluy1">
              View Approaching
            </button>
          </div>
        </div>}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-19736" data-mp="3kucej1u7epbm">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-19737" data-mp="3psxdyz1mt4z0">
          <div data-id="element-19738" data-mp="3bxlk0tvavij5">
            <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-19739" data-mp="3g19qtac3ya3c">
              Pre-Orders
            </h1>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-19740" data-mp="7lq2mpqanhv5">
              Manage pre-order campaigns for your upcoming products
            </p>
          </div>
          <Link to="/vendors/product-manager" className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-19741" data-mp="cdhlsf74elb9">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-19742" data-mp="3521z6dcc1mod" />
            Create Pre-Order
          </Link>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-19743" data-mp="6jd3nv0420mj">
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-19744" data-mp="1ll1m85r4y4m">
            <div className="flex items-center justify-between" data-id="element-19745" data-mp="1jgz9uni476g">
              <div data-id="element-19746" data-mp="3sqbb8njquw2b">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19747" data-mp="3sprxlj14mtu3">
                  Total Pre-Orders
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-19748" data-mp="2he0rf6mzaaa0">
                  {stats.total}
                </p>
              </div>
              <div className={`p-2 rounded-full ${dk.bgMuted}`} data-id="element-19749" data-mp="rvlrdmwnxnhk">
                <PackageIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-19750" data-mp="rt90ulcua4sw" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-19751" data-mp="11ti24dlougf5">
            <div className="flex items-center justify-between" data-id="element-19752" data-mp="11s6uu3t1aqja">
              <div data-id="element-19753" data-mp="16l9soo4ugm5a">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19754" data-mp="16lt03awvbs52">
                  Active
                </p>
                <p className={`text-2xl font-bold mt-1 text-green-600`} data-id="element-19755" data-mp="1fjv9oeuo3t3y">
                  {stats.active}
                </p>
              </div>
              <div className="p-2 rounded-full bg-green-100" data-id="element-19756" data-mp="1khwv36fkg4il">
                <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-19757" data-mp="1phrjn3bvci0h" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-19758" data-mp="3md7357nputcw">
            <div className="flex items-center justify-between" data-id="element-19759" data-mp="3rb8mzusyuwpf">
              <div data-id="element-19760" data-mp="q0j66b9hfmsu">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19761" data-mp="q1b0lircfehj">
                  Upcoming
                </p>
                <p className={`text-2xl font-bold mt-1 text-indigo-600`} data-id="element-19762" data-mp="zyp6wpjjymw4">
                  {stats.upcoming}
                </p>
              </div>
              <div className="p-2 rounded-full bg-indigo-100" data-id="element-19763" data-mp="141lp4f56kgzj">
                <CalendarIcon className="h-5 w-5 text-indigo-600" data-id="element-19764" data-mp="18zx2gzuop9mi" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-19765" data-mp="1dwx859nmgad3">
            <div className="flex items-center justify-between" data-id="element-19766" data-mp="1ioookrd2czpi">
              <div data-id="element-19767" data-mp="1iraxovflrn9u">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19768" data-mp="1nnrsu9l7z1wf">
                  Fulfilled
                </p>
                <p className={`text-2xl font-bold mt-1 text-blue-600`} data-id="element-19769" data-mp="1rnsby7j9k19t">
                  {stats.fulfilled}
                </p>
              </div>
              <div className="p-2 rounded-full bg-blue-100" data-id="element-19770" data-mp="3h7tg52llwqpv">
                <TruckIcon className="h-5 w-5 text-blue-600" data-id="element-19771" data-mp="3c7npnpr2pa9d" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className={`rounded-lg shadow-sm border p-4 mb-6 ${dk.card} ${dk.border}`} data-id="element-19772" data-mp="386lupl46vkd7">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between" data-id="element-19773" data-mp="338tt1zdqsjvt">
            {/* Status Tabs */}
            <div className="w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 scrollbar-hide" data-id="element-19774" data-mp="3vvwr6qeiaslj">
              <div className="flex space-x-2" data-id="element-19775" data-mp="3r4y67dikp6by">
                {['all', 'active', 'upcoming', 'fulfilled', 'cancelled'].map(status => <button key={status} onClick={() => setActiveStatus(status as PreOrderStatus | 'all')} className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeStatus === status ? 'bg-purple-100 text-purple-700' : `${dk.textMuted} hover:${dk.bgMuted}`}`} data-id="element-19776" data-mp="3m5l97zxzmivn">
                      {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>)}
              </div>
            </div>

            {/* Right Side: Type & Search */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" data-id="element-19777" data-mp="ntp15rp43iim">
              <div className="relative min-w-[160px]" data-id="element-19778" data-mp="nrm1efy5qb0p">
                <select value={activeType} onChange={e => setActiveType(e.target.value as ProductType | 'all')} className={`w-full pl-3 pr-8 py-2 text-sm border rounded-md appearance-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-19779" data-mp="9vimdtvuu98t">
                  <option value="all" data-id="element-19780" data-mp="3sr2sowp67ryr">All Types</option>
                  <option value="book" data-id="element-19781" data-mp="3sphy572uw90w">Book</option>
                  <option value="merch" data-id="element-19782" data-mp="3oramurr5vzx3">Merch</option>
                  <option value="bookbox" data-id="element-19783" data-mp="6c9rflag6lqt">Book Box</option>
                  <option value="event" data-id="element-19784" data-mp="6duj935dnqkz">Event</option>
                </select>
                <ChevronDownIcon className={`absolute right-2.5 top-2.5 h-4 w-4 pointer-events-none ${dk.textMuted}`} data-id="element-19785" data-mp="1ki91y1g3mbr" />
              </div>

              <div className="relative flex-grow sm:max-w-xs" data-id="element-19786" data-mp="bbmkwovtqr2d">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-19787" data-mp="bdgz7kclbkxh">
                  <SearchIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-19788" data-mp="p91em5t41l05" />
                </div>
                <input type="text" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-19789" data-mp="k9yb4pss3mua" />
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredPreOrders.length === 0 ? <div className={`text-center py-12 rounded-lg border border-dashed ${dk.border} ${dk.bgMuted}/30`} data-id="element-19790" data-mp="32hbyrm5zk3zf">
            <SearchIcon className={`mx-auto h-12 w-12 ${dk.textMuted}`} data-id="element-19791" data-mp="29t6aipfq3wuu" />
            <h3 className={`mt-2 text-sm font-medium ${dk.textHeading}`} data-id="element-19792" data-mp="29tg4sp7swq6c">
              No pre-orders found
            </h3>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-19793" data-mp="2jizmvsnowg05">
              Try adjusting your search or filters.
            </p>
            <div className="mt-6" data-id="element-19794" data-mp="1m4dnjih2wzcd">
              <button onClick={() => {
            setActiveType('all');
            setActiveStatus('all');
            setSearchQuery('');
          }} className="text-sm font-medium text-purple-600 hover:text-purple-500" data-id="element-19795" data-mp="1m32gn9sc7bh1">
                Clear all filters
              </button>
            </div>
          </div> : <>
            {/* Desktop Table View */}
            <div className={`hidden md:block rounded-lg shadow-sm border overflow-hidden ${dk.card} ${dk.border}`} data-id="element-19796" data-mp="14met5nldr02u">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-19797" data-mp="10luuugp2lqf4">
                <thead className={dk.bgMuted} data-id="element-19798" data-mp="vsthid4ihyxn">
                  <tr data-id="element-19799" data-mp="qui6hz2hdidu">
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19800" data-mp="qtp75j5upv5q">
                      Product
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19801" data-mp="gyf20mk5p7nq">
                      Type
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19802" data-mp="78ypcylzzmox">
                      Pre-Order Price
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19803" data-mp="3uf95cr9ele08">
                      Regular Price
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19804" data-mp="c709znz2nu2i">
                      Orders
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19805" data-mp="19n72pkg9gnmk">
                      Release Date
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-19806" data-mp="1ezvzeocwz1a9">
                      Status
                    </th>
                    <th scope="col" className={`relative px-6 py-3`} data-id="element-19807" data-mp="102v4yd3c8s83">
                      <span className="sr-only" data-id="element-19808" data-mp="1057ouvsnpcns">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${dk.card}`} data-id="element-19809" data-mp="1a1u5f8dy9hk0">
                  {filteredPreOrders.map(item => {
                const discount = item.regularPrice > 0 ? Math.round((1 - item.preOrderPrice / item.regularPrice) * 100) : 0;
                return <tr key={item.id} className={`hover:${dk.bgMuted}/50 transition-colors`} data-id="element-19810" data-mp="eqe6ozy4yc51">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-19811" data-mp="nrccnns4wtq9">
                          <div className="flex items-center" data-id="element-19812" data-mp="sjvn4bviz8qu">
                            <div className="h-10 w-10 flex-shrink-0" data-id="element-19813" data-mp="slgghy63sln4">
                              <img className="h-10 w-10 rounded object-cover" src={item.image} alt="" data-id="element-19814" data-mp="xifc6l5f10nf" />
                            </div>
                            <div className="ml-4" data-id="element-19815" data-mp="1biq5rvy40tca">
                              <div className={`text-sm font-medium ${dk.textHeading}`} data-id="element-19816" data-mp="1bgdm9ecpeax4">
                                {item.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-19817" data-mp="30zvgbevn1g9p">
                          <TypeBadge type={item.type} data-id="element-19818" data-mp="30xing6ocdlhv" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-19819" data-mp="2w123loo6dtal">
                          <div className={`text-sm font-bold ${dk.textHeading}`} data-id="element-19820" data-mp="2r27a9ssl0mc8">
                            {item.preOrderPrice > 0 ? `$${item.preOrderPrice.toFixed(2)}` : 'Free'}
                          </div>
                          {discount > 0 && <div className="text-xs text-green-600 font-medium" data-id="element-19821" data-mp="3apooe5wknd97">
                              {discount}% off
                            </div>}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textSecondary}`} data-id="element-19822" data-mp="1za027gr56i9g">
                          {item.regularPrice > 0 ? `$${item.regularPrice.toFixed(2)}` : '—'}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textSecondary}`} data-id="element-19823" data-mp="pwhu18ca0rkv">
                          {item.orders}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-19824" data-mp="1iijdzm0q9xon">
                          <div className={`text-sm ${dk.textSecondary}`} data-id="element-19825" data-mp="1dmuc3c18k4p6">
                            {item.releaseDate}
                          </div>
                          {item.isApproaching && <div className="text-xs text-red-500 font-medium mt-0.5" data-id="element-19826" data-mp="14kcoat5xn402">
                              Approaching!
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-19827" data-mp="3p9sd5illcin2">
                          <StatusBadge status={item.status} data-id="element-19828" data-mp="24ucchbzju3c5" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-19829" data-mp="29t7515nbj69g">
                          <div className="flex items-center justify-end space-x-3" data-id="element-19830" data-mp="2dsobo2ehe9g7">
                            <button className={`text-gray-400 hover:${dk.textHeading}`} title="View" data-id="element-19831" data-mp="2ikz82va0jd40">
                              <EyeIcon className="h-4 w-4" data-id="element-19832" data-mp="2si3zpammo0s7" />
                            </button>
                            <Link to={`/vendors/product-manager?type=${item.type}&id=${item.id}&returnTo=/vendors/preorders`} className="text-purple-600 hover:text-purple-900" title="Edit" data-id="element-19833" data-mp="2xh6um28w8qmg">
                              <EditIcon className="h-4 w-4" data-id="element-19834" data-mp="174iruzqmwsg3" />
                            </Link>
                            {(item.status === 'active' || item.status === 'upcoming') && <button className={`text-gray-400 hover:text-red-500`} title="Cancel" data-id="element-19835" data-mp="25ievnpk1ge62">
                                <XCircleIcon className="h-4 w-4" data-id="element-19836" data-mp="2fe88la1o69od" />
                              </button>}
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4" data-id="element-19837" data-mp="2y5snxacsklju">
              {filteredPreOrders.map(item => <div key={item.id} className={`relative rounded-lg shadow-sm border p-4 ${dk.card} ${dk.border} ${item.isApproaching ? 'border-l-4 border-l-red-500' : ''}`} data-id="element-19838" data-mp="3c5af1wdk8awd">
                  <div className="flex gap-4" data-id="element-19839" data-mp="3qtaflq9vhhq6">
                    <div className="flex-shrink-0" data-id="element-19840" data-mp="3qq674gpdjztp">
                      <img src={item.image} alt="" className={`rounded object-cover ${item.type === 'book' ? 'w-14 h-[72px]' : 'w-14 h-14'}`} data-id="element-19841" data-mp="3vqakvyywegg8" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-19842" data-mp="381rexq3bmfrb">
                      <div className="flex justify-between items-start" data-id="element-19843" data-mp="3806jly96msse">
                        <div data-id="element-19844" data-mp="3csr7yk9bkdoj">
                          <h3 className={`text-sm font-medium truncate pr-2 ${dk.textHeading}`} data-id="element-19845" data-mp="3cu2g0w9ss7lg">
                            {item.title}
                          </h3>
                          <div className="mt-1 flex items-center gap-2" data-id="element-19846" data-mp="330bqdo1xpr46">
                            <TypeBadge type={item.type} data-id="element-19847" data-mp="2p3f0ppxjyfov" />
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-19848" data-mp="2u42vmmowjgod">
                          <button className={`p-1 rounded-full hover:${dk.bgMuted} ${dk.textMuted}`} data-id="element-19849" data-mp="2lzz6cx4jc1hc">
                            <MoreVerticalIcon className="h-4 w-4" data-id="element-19850" data-mp="2cbh7ilftg4gc" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-2 items-center" data-id="element-19851" data-mp="289duvtheutth">
                        <StatusBadge status={item.status} data-id="element-19852" data-mp="23blueb2p9zdo" />
                        <span className={`text-xs ${dk.textSecondary}`} data-id="element-19853" data-mp="1ybzgs0iymh4c">
                          {item.releaseDate}
                        </span>
                      </div>

                      {item.isApproaching && <p className="mt-2 text-xs text-red-600 flex items-center" data-id="element-19854" data-mp="1pbt4ghyzzn6r">
                          <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-19855" data-mp="3nr5wtjgjlfkb" />
                          Release Approaching!
                        </p>}

                      <div className="mt-3 flex items-center justify-between border-t pt-3 dark:border-gray-700" data-id="element-19856" data-mp="3jn7yvc2yeasu">
                        <div className={`text-xs ${dk.textMuted}`} data-id="element-19857" data-mp="1age74losn1y4">
                          <span className={`font-medium ${dk.textHeading}`} data-id="element-19858" data-mp="1aejlf55p75sz">
                            {item.preOrderPrice > 0 ? `$${item.preOrderPrice.toFixed(2)}` : 'Free'}
                          </span>
                          <span className="mx-2" data-id="element-19859" data-mp="mjqqgvv27tnh">•</span>
                          <span data-id="element-19860" data-mp="rhie3doo8tlv">{item.orders} orders</span>
                        </div>
                        <Link to={`/vendors/product-manager?type=${item.type}&id=${item.id}&returnTo=/vendors/preorders`} className="text-xs font-medium text-purple-600 hover:text-purple-500 flex items-center" data-id="element-19861" data-mp="rjd1qzrbuztn">
                          <EditIcon className="h-3 w-3 mr-1" data-id="element-19862" data-mp="270d7bcp1fjt5" />
                          Edit
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </>}
      </div>
    </div>;
};
export default PreOrders;